import beTwMerge from '@/utils/beTwMerge';
import Image from 'next/image';
import { getAnchorId, getComponentMargin } from '@/utils/builderUtils';

export default function MoissaniteDiamondTable(props) {
  const columnHeaders = [
    {
      description:
        "<div><p class='tw-my-2 tw-text-lg'>Durability</p> <p class='tw-font-light tw-m-0 tw-pb-2 tw-text-sm'>Gemstones are measured on the Mohs scale, which addresses a gem's hardness.</p></div>",
    },
    {
      description:
        "<div><p class='tw-my-2 tw-text-lg'>Brilliance</p> <p class='tw-font-light tw-m-0 tw-pb-2 tw-text-sm'>Brilliance refers to the appearance of light reflected from the gemstone's interior.</p></div>",
    },
    {
      description:
        "<div><p class='tw-my-2 tw-text-lg'>Color</p> <p class='tw-font-light tw-m-0 tw-pb-2 tw-text-sm'>Color is the presence or absence of color visible within a gemstone.</p></div>",
    },
    {
      description:
        "<div><p class='tw-my-2 tw-text-lg'>Price</p> <p class='tw-font-light tw-m-0 tw-pb-2 tw-text-sm'>Price references how much you will pay for a gemstone.</p></div>",
    },
    {
      description:
        "<div><p class='tw-my-2 tw-text-lg'>Sourcing</p> <p class='tw-font-light tw-m-0 tw-pb-2 tw-text-sm'>Sourcing refers to the origin of the gemstone and how it was produced.</p></div>",
    },
  ];
  const moissaniteColumn = [
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Moissanites are at 9.25 on the Mohs scale, making them suitable for everyday wear.",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Moissanites are slightly more brilliant than diamonds, though they feature more than twice the amount of fire. If a moissanite is larger than 1 carat, the difference in fire can be very noticeable as it has a refractive index of 2.64 to 2.69.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>While most moissanites can now be grown as colorless or near colorless, they are also created in hues of teal, grey, green, yellow, and black.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Moissanite is dramatically lower in price than diamonds of the same carat weight. Their pricing is based on size, shape, and whether the stone is colorless or near colorless.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Moissanite is grown in controlled laboratories by professionals. Once formed, they're cut and polished.</p>",
    },
  ];
  const diamondColumn = [
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Diamonds, the hardest known naturally occurring mineral, are a 10 on the Mohs scale, making them the ideal option for engagement rings.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Natural diamonds are slightly less brilliant than moissanite and feature two times less fire, though they still exhibit dispersion and scintillation. The refractive index of diamonds ranges from 2.417 to 2.419.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Natural diamonds occur in the colorless range, but they also occur in many colors, including <a href='https://www.brilliantearth.com/gemstones/colored-diamond/pink/' class='tw-underline'>pink</a>, <a class='tw-underline' href='https://www.brilliantearth.com/gemstones/colored-diamond/blue/'>blue</a>, and <a href='https://www.brilliantearth.com/gemstones/colored-diamond/yellow/' class='tw-underline'>yellow</a> from trace elements like nitrogen and boron.</p>",
    },
    {
      description: `<p class='tw-text-xs lg:tw-text-base'>Natural diamonds are the most expensive. As they are mined from the Earth and take billions of years to form, they are much rarer than both moissanite and lab diamonds. In addition to their rarity, diamonds are priced based on their <a href="https://www.brilliantearth.com/guides/4-cs-of-diamonds/" class="tw-underline">4 C's</a> and <a href="https://www.brilliantearth.com/guides/diamond-shape/" class="tw-underline">shape</>.</p>`,
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Natural diamonds form deep in the Earth. Then, they are mined, cut, and polished.</p>",
    },
  ];
  const labDiamondColumn = [
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Lab diamonds are a 10 on the Mohs scale, making them the ideal option for engagement rings.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Lab diamonds are slightly less brilliant than moissanite and feature two times less fire, though they still exhibit dispersion and scintillation. The refractive index of diamonds ranges from 2.417 to 2.419.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>The rarest lab diamonds are those in the colorless range, but they are also created in many colors. <a class='tw-underline' href='https://www.brilliantearth.com/lab-grown-diamonds/colored/'>Lab grown colored diamonds</a> are more accessible than <a href='https://www.brilliantearth.com/diamond/colored/' class='tw-underline'>natural colored diamonds</a> and can often be found in more vivid, fancier colors.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Lab diamonds cost less than their natural counterparts due to their origin but cost more than moissanite. As with natural diamonds, lab diamonds are priced based on their 4 C's and shape.</p>",
    },
    {
      description:
        "<p class='tw-text-xs lg:tw-text-base'>Lab diamonds are grown in controlled laboratories by professionals. Once formed, they're cut and polished.</p>",
    },
  ];

  return (
    <div
      {...getAnchorId(props.anchorLinkID)}
      className={beTwMerge('tw-mx-2 lg:tw-mx-auto tw-max-w-[1130px]', getComponentMargin(props.margin))}
    >
      <h2 className="tw-text-center tw-mb-12">Moissanite vs. Diamond vs. Lab Diamond</h2>

      <div className="tw-hidden lg:tw-block">
        <table className="tw-table-auto tw-w-full">
          <thead>
            <tr>
              <th />
              <th className="tw-text-center">Moissanite</th>
              <th className="tw-text-center">Diamond</th>
              <th className="tw-text-center">Lab Diamond</th>
            </tr>
          </thead>
          <tbody>
            {columnHeaders.map((column, index) => (
              <tr
                className="tw-border tw-w-full tw-border-solid tw-border-[#ccc] tw-border-r-0 tw-border-l-0"
                key={column.description.replace(' ', '-')}
              >
                <td
                  className="tw-text-center tw-w-3/12 tw-p-9 tw-pl-0"
                  dangerouslySetInnerHTML={{ __html: column.description }}
                />
                <td
                  className="tw-bg-[#f9f9f9] tw-w-3/12 tw-p-9 tw-text-center"
                  dangerouslySetInnerHTML={{ __html: moissaniteColumn[index].description }}
                />
                <td
                  className="tw-p-9 tw-w-3/12 tw-text-center"
                  dangerouslySetInnerHTML={{ __html: diamondColumn[index].description }}
                />
                <td
                  className="tw-bg-[#f9f9f9] tw-w-3/12 tw-p-9 tw-text-center"
                  dangerouslySetInnerHTML={{ __html: labDiamondColumn[index].description }}
                />
              </tr>
            ))}
            <tr className="tw-h-full">
              <td className="tw-flex tw-justify-center tw-items-center tw-h-full">
                <Image
                  width={64}
                  height={64}
                  className="tw-w-16"
                  src="/bee2-ui/img/icon/360-Diamond-Icon-v3.jpg"
                  alt="360 Rotation"
                />
              </td>
              <td>
                <video controls className="tw-w-full" loop muted preload="metadata" playsInline>
                  <source src={`${props.moissaniteVideo}#t=0.001`} type="video/mp4" />
                </video>
              </td>
              <td>
                <video controls className="tw-w-full" loop muted preload="metadata" playsInline>
                  <source src={`${props.diamondVideo}#t=0.001`} type="video/mp4" />
                </video>
              </td>
              <td>
                <video controls className="tw-w-full" loop muted preload="metadata" playsInline>
                  <source src={`${props.labDiamondVideo}#t=0.001`} type="video/mp4" />
                </video>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tw-block lg:tw-hidden">
        <table>
          {columnHeaders.map((column, index) => (
            <>
              <thead>
                <tr>
                  <th colSpan={3} className="tw-text-center" dangerouslySetInnerHTML={{ __html: column.description }} />
                </tr>
              </thead>
              <tbody>
                <tr className="tw-border tw-w-full tw-border-solid tw-border-[#ccc] tw-border-t-0 tw-border-l-0 tw-border-r-0">
                  <td className="tw-bg-[#f9f9f9] tw-text-center tw-p-2 tw-pb-5 tw-w-1/3 tw-align-top" colSpan={1}>
                    <p>Moissanite</p>
                    <div dangerouslySetInnerHTML={{ __html: moissaniteColumn[index].description }} />
                  </td>
                  <td className="tw-text-center tw-p-2 tw-pb-5 tw-w-1/3 tw-align-top" colSpan={1}>
                    <p>Diamond</p>
                    <div dangerouslySetInnerHTML={{ __html: diamondColumn[index].description }} />
                  </td>
                  <td className="tw-bg-[#f9f9f9] tw-text-center tw-p-2 tw-pb-5 tw-w-1/3 tw-align-top" colSpan={1}>
                    <p>Lab Diamond</p>
                    <div dangerouslySetInnerHTML={{ __html: labDiamondColumn[index].description }} />
                  </td>
                </tr>
              </tbody>
            </>
          ))}
          <tr>
            <td colSpan={3} className="tw-w-full">
              <div className="tw-flex tw-justify-center tw-items-center">
                <img className="tw-w-16" src="/bee2-ui/img/icon/360-Diamond-Icon-v3.jpg" alt="360 Rotation" />
              </div>
            </td>
          </tr>
          <tr>
            <td className="tw-w-full tw-px-5" colSpan={3}>
              <p className="tw-text-center tw-text-[15px] tw-my-0">Moissanite</p>
              <video controls className="tw-w-full tw-my-2" loop muted preload="metadata" playsInline>
                <source src={`${props.moissaniteVideo}#t=0.001`} type="video/mp4" />
              </video>
            </td>
          </tr>
          <tr>
            <td className="tw-w-full tw-px-5" colSpan={3}>
              <p className="tw-text-center tw-text-[15px] tw-my-0">Diamond</p>
              <video controls className="tw-w-full tw-my-2" loop muted preload="metadata" playsInline>
                <source src={`${props.diamondVideo}#t=0.001`} type="video/mp4" />
              </video>
            </td>
          </tr>
          <tr>
            <td className="tw-w-full tw-px-5" colSpan={3}>
              <p className="tw-text-center tw-text-[15px] tw-my-0">Lab Diamond</p>
              <video controls className="tw-w-full tw-my-2" loop muted preload="metadata" playsInline>
                <source src={`${props.labDiamondVideo}#t=0.001`} type="video/mp4" />
              </video>
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
}
